import { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import GoldenParticles from "@/components/GoldenParticles";
import { api } from "@/lib/api";

/**
 * ScanReveal — luxury watch-box reveal animation shown the FIRST time a visitor
 * lands on a public page via QR/NFC scan. Storyboard:
 *
 *   0.0s — 0.4s : Ignition (radial gold glow blooms outward)
 *   0.4s — 1.2s : The Object (trophy/medal/award fades in with metallic shine)
 *   1.2s — 1.8s : Ascension (object shrinks/translates upward, content slides up)
 *   1.8s — 2.2s : Information Bloom (children render, particles persist)
 *
 * Per-slug dedup via sessionStorage so we only "wow" once per scan.
 *
 * Props:
 *  - slug:    unique key for dedup (player slug / team slug / tournament slug)
 *  - kind:    "player" | "team" | "tournament"
 *  - accent:  optional gold-override (defaults to #D4AF37)
 *  - children: the actual page content (rendered AFTER reveal completes)
 */
export default function ScanReveal({
  slug,
  kind = "player",
  accent = "#D4AF37",
  children,
  disabled = false,
}) {
  const reduced = useReducedMotion();
  const storageKey = `ltc_scan_revealed_${kind}_${slug || "unknown"}`;

  useEffect(() => {
    if (!slug) return;
    const trackKey = `ltc_view_tracked_${kind}_${slug}`;
    try {
      if (sessionStorage.getItem(trackKey) === "1") return;
      sessionStorage.setItem(trackKey, "1");
    } catch {
      /* no-op */
    }
    api.post("/track/view", { kind, slug }).catch(() => {});
  }, [kind, slug]);

  const [showOverlay, setShowOverlay] = useState(() => {
    if (disabled || reduced) return false;
    if (typeof window === "undefined") return false;
    try {
      return sessionStorage.getItem(storageKey) !== "1";
    } catch {
      return true;
    }
  });

  useEffect(() => {
    if (!showOverlay) return;
    const t = setTimeout(() => {
      try {
        sessionStorage.setItem(storageKey, "1");
      } catch {
        /* noop */
      }
      setShowOverlay(false);
    }, 2200);
    return () => clearTimeout(t);
  }, [showOverlay, storageKey]);

  return (
    <>
      <AnimatePresence>
        {showOverlay && (
          <motion.div
            key="scan-reveal-overlay"
            data-testid="scan-reveal-overlay"
            className="fixed inset-0 z-[100] flex items-center justify-center bg-[#050505] overflow-hidden"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Step 1: Ignition — radial gold burst */}
            <motion.div
              className="absolute inset-0 reveal-radial"
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: [0, 0.9, 0.55], scale: [0.6, 1.25, 1.05] }}
              transition={{
                duration: 1.8,
                times: [0, 0.35, 1],
                ease: [0.22, 1, 0.36, 1],
              }}
            />

            {/* Floating gold particles */}
            <GoldenParticles count={22} density="dense" />

            {/* Step 2-3: The Object — trophy/medal/award */}
            <motion.div
              className="relative z-10 flex flex-col items-center"
              initial={{ opacity: 0, scale: 0.7, y: 0 }}
              animate={{
                opacity: [0, 1, 1, 0.92],
                scale: [0.7, 1, 1, 0.42],
                y: [0, 0, 0, -90],
              }}
              transition={{
                duration: 2.0,
                times: [0, 0.25, 0.6, 1],
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              {/* Inner glow ring */}
              <motion.div
                className="absolute -inset-12 rounded-full"
                style={{
                  background:
                    "radial-gradient(circle, rgba(249,223,159,0.35) 0%, rgba(212,175,55,0.15) 35%, rgba(5,5,5,0) 70%)",
                  filter: "blur(8px)",
                }}
                animate={{ opacity: [0.4, 0.9, 0.5] }}
                transition={{ duration: 1.8, ease: "easeInOut" }}
              />
              <div
                className="relative w-56 sm:w-72 flex items-center justify-center"
              >
                {/* Lueur animée via drop-shadow : suit la forme exacte du logo, aucun rectangle possible */}
                <motion.img
                  src="/logo-splash.png"
                  alt="Laser Tech Création"
                  className="w-full h-auto object-contain"
                  initial={{
                    filter:
                      "drop-shadow(0 0 18px rgba(212,175,55,0.35)) drop-shadow(0 8px 30px rgba(249,223,159,0.2))",
                  }}
                  animate={{
                    filter: [
                      "drop-shadow(0 0 18px rgba(212,175,55,0.35)) drop-shadow(0 8px 30px rgba(249,223,159,0.2))",
                      "drop-shadow(0 0 42px rgba(212,175,55,0.75)) drop-shadow(0 12px 60px rgba(249,223,159,0.45))",
                      "drop-shadow(0 0 26px rgba(212,175,55,0.5)) drop-shadow(0 10px 44px rgba(249,223,159,0.3))",
                    ],
                  }}
                  transition={{ duration: 1.6, delay: 0.3, times: [0, 0.5, 1], ease: "easeInOut" }}
                />
              </div>
              {/* Caption */}
              <motion.div
                className="mt-6 text-center"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: [0, 1, 1, 0] }}
                transition={{
                  duration: 1.8,
                  times: [0, 0.35, 0.85, 1],
                  delay: 0.3,
                }}
              >
                <div
                  className="text-[10px] font-bold uppercase tracking-[0.4em] mb-1"
                  style={{ color: accent }}
                >
                  Laser Tech Création
                </div>
                <div className="font-display text-2xl font-semibold text-white tracking-tight">
                  {kind === "team"
                    ? "Équipe connectée"
                    : kind === "tournament"
                      ? "Tournoi connecté"
                      : kind === "championship"
                        ? "Championnat connecté"
                        : kind === "association"
                          ? "Association connectée"
                          : kind === "company"
                            ? "Entreprise connectée"
                            : "Médaille connectée"}
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content — fades in as overlay exits */}
      <motion.div
        initial={{ opacity: showOverlay ? 0 : 1, y: showOverlay ? 24 : 0 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: 0.7,
          delay: showOverlay ? 1.6 : 0,
          ease: [0.22, 1, 0.36, 1],
        }}
        data-testid="scan-reveal-content"
      >
        {children}
      </motion.div>
    </>
  );
}
